import React, { useEffect, useState, useCallback } from "react";
import SuggestionsDataTable from "./components/SuggestionsDataTable";
import Loader from "components/Loader";
import { suggestionsApi, Suggestion, SuggestionsListParams, SuggestionType, SuggestionStatus } from "api/suggestions";
import { handleAxiosAndCaughtError } from "utils";
import { useParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

// Local enum for tab types
enum TabType {
  AUTO_GENERATED = 'auto-generated',
  FREQUENT = 'frequent'
}

const Suggestions = () => {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [suggestionStatus, setSuggestionStatus] = useState<SuggestionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>(TabType.AUTO_GENERATED);
  const { id: agentId } = useParams<{ id: string }>();

  const fetchSuggestions = useCallback(async (signal?: AbortSignal, showFullLoader = true) => {
    if (!agentId) return;
    
    if (showFullLoader) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);
    try {
      const params: SuggestionsListParams = {
        type: activeTab === TabType.AUTO_GENERATED ? SuggestionType.INITIALIZE : SuggestionType.FREQUENT
      };
      
      const resp = await suggestionsApi.getList(agentId, params);
      setSuggestions(resp.suggestions);
      setSuggestionStatus(resp.status);
    } catch (err: any) {
      if (signal?.aborted || err.name === "CanceledError") {
        return; // Aborted
      }
      setError(t("suggestions.failedToFetch"));
      handleAxiosAndCaughtError(err);
    } finally {
      if (showFullLoader) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  }, [agentId, activeTab, t]);

  const handleRefresh = async () => {
    if (!agentId) return;
    
    try {
      setRefreshing(true);
      setError(null);
      
      // Clear current list
      setSuggestions([]);
      setSuggestionStatus(null);
      
      // Call regenerate API
      await suggestionsApi.regenerate(agentId);
      
      // Fetch new list
      await fetchSuggestions(undefined, false);
    } catch (err: any) {
      setError(t("suggestions.failedToRegenerate"));
      handleAxiosAndCaughtError(err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchSuggestions(abortController.signal);
    return () => abortController.abort();
  }, [fetchSuggestions]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="mb-4 mt-2 flex w-full flex-col items-center border-b border-[#ececec] shadow-2xl shadow-white dark:border-navy-700 dark:!shadow-none xl:mb-3">
        <div className="flex w-full justify-between items-center overflow-hidden text-3xl">
          <div className="flex justify-start gap-11">
            <button
              onClick={() => handleTabChange(TabType.AUTO_GENERATED)}
              className={`flex items-center gap-2 border-b-[4px] pb-3 hover:cursor-pointer ${
                activeTab === TabType.AUTO_GENERATED
                  ? "border-brand-500 dark:border-brand-400"
                  : "border-white dark:!border-navy-800"
              }`}
            >
              <h5 className="text-base font-medium text-navy-700 dark:text-white">
                {t("agents.autoGenerated")}
              </h5>
            </button>
            <button
              onClick={() => handleTabChange(TabType.FREQUENT)}
              className={`flex items-center gap-2 border-b-[4px] pb-3 hover:cursor-pointer ${
                activeTab === TabType.FREQUENT
                  ? "border-brand-500 dark:border-brand-400"
                  : "border-white dark:!border-navy-800"
              }`}
            >
              <h5 className="text-base font-medium text-navy-700 dark:text-white">
                {t("agents.frequent")}
              </h5>
            </button>
          </div>
          {activeTab === TabType.AUTO_GENERATED && (
            <button
              onClick={handleRefresh}
              disabled={loading || refreshing || (activeTab === TabType.AUTO_GENERATED && suggestionStatus === SuggestionStatus.PROCESSING)}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
              title={t("agents.regenerateList")}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {t("agents.regenerate")}
            </button>
          )}
        </div>
      </div>

      {loading || (activeTab === TabType.AUTO_GENERATED && suggestionStatus === SuggestionStatus.PROCESSING) ? (
        <Loader
          txt={activeTab === TabType.AUTO_GENERATED && suggestionStatus === SuggestionStatus.PROCESSING 
            ? t("agents.generatingSuggestions", { type: t("agents.autoGenerated") }) 
            : t("agents.loadingSuggestions", { type: activeTab === TabType.AUTO_GENERATED ? t("agents.autoGenerated") : t("agents.frequent") })}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      ) : error ? (
        <div
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2
        -translate-y-1/2 flex-col items-center justify-center py-10
         "
        >
          <span className="mb-4 text-red-500">{error}</span>
          <button
            className="rounded bg-brand-500 px-4 py-2 text-white transition hover:bg-brand-600"
            onClick={() => fetchSuggestions()}
          >
            {t("common.retry")}
          </button>
        </div>
      ) : (
        <SuggestionsDataTable 
          fetchSuggestions={fetchSuggestions} 
          tableData={suggestions || []} 
          agentId={agentId}
          suggestionType={activeTab === TabType.AUTO_GENERATED ? SuggestionType.INITIALIZE : SuggestionType.FREQUENT}
        />
      ) }
    </div>
  );
};

export default Suggestions;
